import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, keyframes, useTheme, useMediaQuery } from "@mui/material";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DescriptionIcon from "@mui/icons-material/Description";

// ============ ANIMATIONS ============
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.9; }
`;

const breathe = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

// ============ COMPACT RANGE BAR ============
const CompactRangeBar = ({ value, min, max, normalMin, normalMax, unit, isMobile = false }) => {
  const totalRange = max - min;
  const normalStartPercent = ((normalMin - min) / totalRange) * 100;
  const normalWidthPercent = ((normalMax - normalMin) / totalRange) * 100;
  const valuePercent = Math.min(Math.max(((value - min) / totalRange) * 100, 2), 98);
  
  const isNormal = value >= normalMin && value <= normalMax;
  const markerColor = isNormal ? "#10b981" : "#ef4444";

  return (
    <Box sx={{ width: "100%", marginTop: isMobile ? "2px" : "4px" }}>
      <Box sx={{ position: "relative", height: isMobile ? "18px" : "24px", display: "flex", alignItems: "flex-end", marginBottom: "2px" }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: isMobile ? "4px" : "6px",
            borderRadius: isMobile ? "2px" : "3px",
            overflow: "visible",
          }}
        >
          {/* Low Range Zone */}
          <Box
            sx={{
              position: "absolute",
              left: "0%",
              width: `${normalStartPercent}%`,
              height: "100%",
              background: "linear-gradient(90deg, #ef4444, #dc2626)",
              borderTopLeftRadius: "3px",
              borderBottomLeftRadius: "3px",
            }}
          />
          
          {/* Normal Range Zone */}
          <Box
            sx={{
              position: "absolute",
              left: `${normalStartPercent}%`,
              width: `${normalWidthPercent}%`,
              height: "100%",
              background: "linear-gradient(90deg, #86efac, #4ade80)",
              boxShadow: "0 1px 4px rgba(16, 185, 129, 0.3)",
            }}
          />
          
          {/* High Range Zone */}
          <Box
            sx={{
              position: "absolute",
              left: `${normalStartPercent + normalWidthPercent}%`,
              width: `${100 - normalStartPercent - normalWidthPercent}%`,
              height: "100%",
              background: "linear-gradient(90deg, #dc2626, #ef4444)",
              borderTopRightRadius: "3px",
              borderBottomRightRadius: "3px",
            }}
          />
          
          {/* Value Marker */}
          <Box
            sx={{
              position: "absolute",
              left: `${valuePercent}%`,
              bottom: "100%",
              transform: "translateX(-50%)",
              marginBottom: "3px",
            }}
          >
            <Box
              sx={{
                background: isNormal 
                  ? "rgba(220, 252, 231, 0.95)" 
                  : "rgba(254, 226, 226, 0.95)",
                backdropFilter: "blur(4px)",
                border: isNormal 
                  ? "1px solid #059669" 
                  : "1px solid #dc2626",
                color: isNormal 
                  ? "#059669" 
                  : "#dc2626",
                padding: isMobile ? "1px 4px" : "2px 6px",
                borderRadius: isMobile ? "3px" : "4px",
                fontSize: isMobile ? "7px" : "8px",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                whiteSpace: "nowrap",
                boxShadow: isNormal 
                  ? "0 2px 8px rgba(5, 150, 105, 0.25)" 
                  : "0 2px 8px rgba(220, 38, 38, 0.25)",
              }}
            >
              {value}{unit}
            </Box>
            <Box
              sx={{
                width: 0,
                height: 0,
                borderLeft: isMobile ? "3px solid transparent" : "4px solid transparent",
                borderRight: isMobile ? "3px solid transparent" : "4px solid transparent",
                borderTop: isNormal 
                  ? `${isMobile ? "4px" : "5px"} solid #059669` 
                  : `${isMobile ? "4px" : "5px"} solid #dc2626`,
                margin: "0 auto",
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ============ SMART REPORT 3D CARD ============
const SmartReport3DCard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  
  // Mouse position tracking for parallax
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Smooth spring animations for 3D tilt
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [8, -8]), { stiffness: 300, damping: 30 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-8, 8]), { stiffness: 300, damping: 30 });
  
  // Depth transforms for parallax layers
  const backgroundZ = useTransform(mouseX, [-0.5, 0.5], [-10, 10]);
  const cardsZ = useTransform(mouseX, [-0.5, 0.5], [-5, 5]);
  const badgesZ = useTransform(mouseX, [-0.5, 0.5], [5, -5]);
  
  // Sample test data
  const testData = [
    { name: "Hemoglobin", value: 13.2, unit: " g/dL", min: 8, max: 18, normalMin: 12, normalMax: 15.5, insight: "Healthy" },
    { name: "WBC Count", value: 85, unit: "/μL", min: 20, max: 150, normalMin: 40, normalMax: 110, insight: "Strong" },
    { name: "Glucose", value: 95, unit: " mg/dL", min: 50, max: 150, normalMin: 70, normalMax: 100, insight: "Normal" },
  ];
  
  const normalCount = testData.filter(item => item.value >= item.normalMin && item.value <= item.normalMax).length;
  const abnormalCount = testData.length - normalCount;
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!cardRef.current || isMobile) return;
      
      const rect = cardRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const x = (e.clientX - centerX) / (rect.width / 2);
      const y = (e.clientY - centerY) / (rect.height / 2);
      
      mouseX.set(x);
      mouseY.set(y);
    };
    
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => {
      setIsHovered(false);
      mouseX.set(0);
      mouseY.set(0);
    };
    
    const card = cardRef.current;
    if (card) {
      card.addEventListener("mousemove", handleMouseMove);
      card.addEventListener("mouseenter", handleMouseEnter);
      card.addEventListener("mouseleave", handleMouseLeave);
    }
    
    return () => {
      if (card) {
        card.removeEventListener("mousemove", handleMouseMove);
        card.removeEventListener("mouseenter", handleMouseEnter);
        card.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, [isMobile, mouseX, mouseY]);
  
  return (
    <Box
      ref={cardRef}
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        perspective: isMobile ? "800px" : "1200px",
        perspectiveOrigin: "center center",
        overflow: "hidden",
        backgroundColor: "#f8fafc",
        cursor: isMobile ? "default" : "pointer",
      }}
    >
      {/* Background Layer - Parallax */}
      <motion.div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          background: "linear-gradient(135deg, rgba(53, 103, 214, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)",
          translateZ: backgroundZ,
          willChange: "transform",
        }}
      />
      
      {/* Main Content Container */}
      <motion.div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          padding: isMobile ? "8px" : "16px",
          gap: isMobile ? "4px" : "8px",
          rotateX: isMobile ? 0 : rotateX,
          rotateY: isMobile ? 0 : rotateY,
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
        animate={isMobile ? {} : {
          y: [0, -4, 0],
          scale: [1, 1.01, 1],
        }}
        transition={isMobile ? {} : {
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        {/* Smart Badge - Parallax Layer */}
        <motion.div
          style={{
            position: "absolute",
            top: isMobile ? "8px" : "12px",
            right: isMobile ? "8px" : "12px",
            translateZ: badgesZ,
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #10b981, #059669)",
              borderRadius: isMobile ? "8px" : "12px",
              padding: isMobile ? "2px 6px" : "3px 8px",
              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
              animation: isMobile ? "none" : `${pulse} 2s ease-in-out infinite`,
            }}
          >
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "7px" : "8px", fontWeight: 700, color: "#fff", textTransform: "uppercase" }}>
              ✨ Smart
            </Typography>
          </Box>
        </motion.div>
        
        {/* Comparison Cards Container */}
        <Box
          sx={{
            display: "flex",
            gap: isMobile ? "4px" : "6px",
            flex: 1,
            transformStyle: "preserve-3d",
            minHeight: 0, // Prevent flex items from growing beyond container
          }}
        >
          {/* Traditional Report Card */}
          <motion.div
            style={{
              flex: 1,
              translateZ: cardsZ,
              willChange: "transform",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                background: "linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)",
                borderRadius: isMobile ? "8px" : "12px",
                padding: isMobile ? "6px" : "8px",
                border: "1.5px solid #e2e8f0",
                display: "flex",
                flexDirection: "column",
                gap: isMobile ? "4px" : "6px",
                minHeight: 0, // Prevent overflow
                overflow: "hidden",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "4px" : "6px", marginBottom: isMobile ? "2px" : "4px", flexShrink: 0 }}>
                <Box
                  sx={{
                    width: isMobile ? "18px" : "24px",
                    height: isMobile ? "18px" : "24px",
                    borderRadius: isMobile ? "6px" : "8px",
                    background: "linear-gradient(145deg, #e2e8f0, #cbd5e1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <DescriptionIcon sx={{ fontSize: isMobile ? "10px" : "12px", color: "#64748b" }} />
                </Box>
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: isMobile ? "8px" : "10px", fontWeight: 700, color: "#475569" }}>
                  Traditional
                </Typography>
              </Box>
              
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: isMobile ? "3px" : "4px", overflow: "auto", minHeight: 0 }}>
                {testData.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      padding: isMobile ? "4px 6px" : "6px 8px",
                      background: "#fff",
                      borderRadius: isMobile ? "4px" : "6px",
                      border: "1px dashed #d1d5db",
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "8px" : "9px", color: "#374151", fontWeight: 500 }}>
                      {item.name}
                    </Typography>
                    <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "9px" : "10px", color: "#1f2937", fontWeight: 700, marginTop: "2px" }}>
                      {item.value}{item.unit}
                    </Typography>
                  </Box>
                ))}
              </Box>
              
              {/* Limitations Card */}
              <Box
                sx={{
                  padding: isMobile ? "4px 6px" : "6px 8px",
                  background: "rgba(226, 232, 240, 0.5)",
                  borderRadius: isMobile ? "4px" : "6px",
                  border: "1px solid #e2e8f0",
                  marginTop: "auto",
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "3px" : "4px", marginBottom: isMobile ? "2px" : "4px" }}>
                  <Box sx={{ width: isMobile ? "2px" : "3px", height: isMobile ? "2px" : "3px", borderRadius: "50%", background: "#94a3b8" }} />
                  <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "7px" : "8px", color: "#64748b", fontWeight: 600 }}>
                    Limitations
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "6px" : "7px", color: "#94a3b8", lineHeight: 1.4 }}>
                  • No visual context<br/>
                  • Requires medical knowledge<br/>
                  • No insights
                </Typography>
              </Box>
            </Box>
          </motion.div>
          
          {/* Smart Report Card */}
          <motion.div
            style={{
              flex: 1,
              translateZ: cardsZ,
              willChange: "transform",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                background: "linear-gradient(145deg, #ffffff 0%, #f0fdf4 100%)",
                borderRadius: isMobile ? "8px" : "12px",
                padding: isMobile ? "6px" : "8px",
                border: "2px solid #86efac",
                display: "flex",
                flexDirection: "column",
                gap: isMobile ? "4px" : "6px",
                boxShadow: "0 8px 24px rgba(16, 185, 129, 0.15)",
                minHeight: 0, // Prevent overflow
                overflow: "hidden",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "4px" : "6px", marginBottom: isMobile ? "2px" : "4px", flexShrink: 0 }}>
                <Box
                  sx={{
                    width: isMobile ? "18px" : "24px",
                    height: isMobile ? "18px" : "24px",
                    borderRadius: isMobile ? "6px" : "8px",
                    background: "linear-gradient(135deg, #3567D6, #5B8DEF)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(53, 103, 214, 0.35)",
                    animation: isMobile ? "none" : `${float} 3s ease-in-out infinite`,
                    flexShrink: 0,
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: isMobile ? "10px" : "12px", color: "#fff" }} />
                </Box>
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: isMobile ? "8px" : "10px", fontWeight: 700, color: "#0f172a" }}>
                  Smart
                </Typography>
              </Box>
              
              {/* Status Badges */}
              <Box sx={{ display: "flex", gap: isMobile ? "3px" : "4px", flexWrap: "wrap", flexShrink: 0 }}>
                {normalCount > 0 && (
                  <Box
                    sx={{
                      background: "linear-gradient(135deg, #dcfce7, #d1fae5)",
                      borderRadius: isMobile ? "4px" : "6px",
                      padding: isMobile ? "2px 4px" : "3px 6px",
                      display: "flex",
                      alignItems: "center",
                      gap: isMobile ? "2px" : "3px",
                      border: "1px solid #86efac",
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: isMobile ? "8px" : "10px", color: "#15803d" }} />
                    <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "7px" : "8px", fontWeight: 600, color: "#15803d" }}>
                      Normal {normalCount}
                    </Typography>
                  </Box>
                )}
                {abnormalCount > 0 && (
                  <Box
                    sx={{
                      background: "linear-gradient(135deg, #fef3c7, #fde68a)",
                      borderRadius: isMobile ? "4px" : "6px",
                      padding: isMobile ? "2px 4px" : "3px 6px",
                      display: "flex",
                      alignItems: "center",
                      gap: isMobile ? "2px" : "3px",
                      border: "1px solid #fcd34d",
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: isMobile ? "8px" : "10px", color: "#b45309" }} />
                    <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "7px" : "8px", fontWeight: 600, color: "#b45309" }}>
                      Abnormal {abnormalCount}
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {/* Smart Parameters */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: isMobile ? "4px" : "6px", overflow: "auto", minHeight: 0 }}>
                {testData.map((item, idx) => {
                  const isNormal = item.value >= item.normalMin && item.value <= item.normalMax;
                  return (
                    <Box
                      key={idx}
                      sx={{
                        background: isNormal 
                          ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)" 
                          : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                        border: `1.5px solid ${isNormal ? "#86efac" : "#fcd34d"}`,
                        borderRadius: isMobile ? "6px" : "8px",
                        padding: isMobile ? "4px" : "6px",
                        flexShrink: 0,
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2px" }}>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: isMobile ? "8px" : "9px", fontWeight: 700, color: "#1e293b" }}>
                          {item.name}
                        </Typography>
                        {isNormal ? (
                          <CheckCircleIcon sx={{ fontSize: isMobile ? "8px" : "10px", color: "#10b981" }} />
                        ) : (
                          <WarningAmberIcon sx={{ fontSize: isMobile ? "8px" : "10px", color: "#f59e0b" }} />
                        )}
                      </Box>
                      <CompactRangeBar
                        value={item.value}
                        min={item.min}
                        max={item.max}
                        normalMin={item.normalMin}
                        normalMax={item.normalMax}
                        unit={item.unit}
                        isMobile={isMobile}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </motion.div>
        </Box>
      </motion.div>
    </Box>
  );
};

export default SmartReport3DCard;

